import type { CSSProperties } from 'react';
import { cx } from '@/lib/cx';
import styles from './SacredGeometry.module.css';

export interface SacredGeometryProps {
  /** `flower` flor de la vida · `compass` rosa de los vientos · `metatron` red. */
  variant?: 'flower' | 'compass' | 'metatron';
  size?: number;
  className?: string;
  style?: CSSProperties;
}

const R = 12;
const CENTER = 50;

function ring(radius: number, count: number, phase = 0) {
  return Array.from({ length: count }, (_, i) => {
    const angle = phase + (i * 2 * Math.PI) / count;
    return { cx: CENTER + radius * Math.cos(angle), cy: CENTER + radius * Math.sin(angle) };
  });
}

/**
 * Geometria sagrada en SVG propio (ADR-004 §1.3: un ornamento geometrico NO se
 * genera como imagen). Se calcula, asi que escala perfecta y pesa nada.
 */
export function SacredGeometry({ variant = 'flower', size = 220, className, style }: SacredGeometryProps) {
  const flower = [{ cx: CENTER, cy: CENTER }, ...ring(R, 6), ...ring(R * Math.sqrt(3), 6, Math.PI / 6)];
  const spokes = ring(38, 8);
  const nodes = ring(30, 6, -Math.PI / 2);

  return (
    <svg
      className={cx(styles.geometry, className)}
      viewBox="0 0 100 100"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={0.5}
      aria-hidden="true"
      focusable="false"
      style={style}
    >
      <circle cx={CENTER} cy={CENTER} r={46} className={styles.frame} />
      {variant === 'flower' ? flower.map((c, i) => <circle key={i} cx={c.cx} cy={c.cy} r={R} />) : null}
      {variant === 'compass' ? (
        <>
          <circle cx={CENTER} cy={CENTER} r={30} />
          <circle cx={CENTER} cy={CENTER} r={16} />
          {spokes.map((p, i) => (
            <line key={i} x1={CENTER} y1={CENTER} x2={p.cx} y2={p.cy} />
          ))}
        </>
      ) : null}
      {variant === 'metatron' ? (
        <>
          {nodes.map((p, i) => (
            <circle key={`n${i}`} cx={p.cx} cy={p.cy} r={4} />
          ))}
          {nodes.map((a, i) =>
            nodes
              .slice(i + 1)
              .map((b, j) => <line key={`l${i}-${j}`} x1={a.cx} y1={a.cy} x2={b.cx} y2={b.cy} />),
          )}
          <circle cx={CENTER} cy={CENTER} r={4} />
        </>
      ) : null}
    </svg>
  );
}
